import { Link } from '@tanstack/react-router'
import { ChevronDownIcon, LogOut, Shield, User } from 'lucide-react'
import { useState } from 'react'
import { Button } from './ui/button'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import type { CurrentUserResponse } from '@/tygo/dto'
import { useLogoutMutation } from '@/domains/auth/queries'
import { USDCToString } from '@/lib/currency'
import { useBalance } from '@/domains/balance/hooks/useBalance'
import { cn } from '@/lib/utils'
import { useUserQuery } from '@/domains/user/queries'

export function NavBar() {
  const { data: user } = useUserQuery()

  return (
    <header className="w-full flex items-center justify-between lg:px-8 px-3 py-4">
      <Link to="/" className="font-black italic">
        Powka
      </Link>
      {user ? <AuthenticatedMenu user={user} /> : <UnauthenticatedMenu />}
    </header>
  )
}

function AuthenticatedMenu({ user }: { user: CurrentUserResponse }) {
  const { mutate: logout } = useLogoutMutation()
  const balance = useBalance()
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm leading-none font-medium">
        ${USDCToString(balance ?? 0)}
      </span>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger>
          <Button variant={'ghost'}>
            <span className="text-sm leading-none font-medium">
              {user.user.username}
            </span>
            <ChevronDownIcon
              className={cn(
                'w-4 h-4 transition-transform duration-200 mt-[2px]',
                isOpen && '-rotate-90',
              )}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-fit h-fit p-2">
          <ul>
            <Link
              to="/account"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-2 text-sm hover:bg-secondary/90 p-2 px-4 rounded-sm font-medium"
            >
              <User color="black" className="size-4" />
              Account
            </Link>
            {user.user.is_admin && (
              <Link
                to="/admin"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-2 text-sm hover:bg-secondary/90 p-2 px-4 rounded-sm font-medium"
              >
                <Shield color="black" className="size-4" />
                Admin
              </Link>
            )}
            <Button
              onClick={() => {
                logout()
                setIsOpen(false)
              }}
              variant={'ghost'}
              className="flex items-center gap-2 text-sm hover:bg-secondary/90 p-2 px-4 rounded-sm"
            >
              <LogOut color="black" className="size-4" />
              Sign out
            </Button>
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  )
}

function UnauthenticatedMenu() {
  return (
    <div className="flex items-center">
      <Button variant="link" asChild>
        <Link to="/login" className="flex items-center gap-2 font-medium">
          Login
        </Link>
      </Button>
      <Button variant="steelGold" asChild>
        <Link to="/signup" className="flex items-center gap-2 font-medium">
          Get Started
        </Link>
      </Button>
    </div>
  )
}
